import { renderVerificationStatus } from "@utils/clinic";
import { formatAddress, toTitleCase } from "@utils/strings";
import { format } from "date-fns";
import { useParams } from "react-router-dom";

import { Heading, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  type GetClinicListingsOutput,
  type GetOrganizationsOutput,
  VerificationStatusEnum,
  useBasecampClinicListingsRetrieve,
  useBasecampOrganizationsRetrieve,
} from "@api/rest/types-and-hooks";
import { Breadcrumb } from "@components/Breadcrumb/Breadcrumb";

export const ClinicDetailPage: React.FC = () => {
  const { orgId, clinicId } = useParams<{ orgId: string; clinicId: string }>();
  const { data: orgData } = useBasecampOrganizationsRetrieve({ id: orgId }, { query: { enabled: !!orgId } });
  const org: GetOrganizationsOutput | undefined =
    orgData?.status === 200
      ? Array.isArray(orgData.data)
        ? orgData.data.find((o) => String(o.id) === orgId)
        : orgData.data
      : undefined;

  const { data: clinicsData, isLoading } = useBasecampClinicListingsRetrieve(
    { org_id: orgId },
    { query: { enabled: !!orgId } },
  );
  const clinics: GetClinicListingsOutput[] =
    clinicsData?.status === 200 ? (Array.isArray(clinicsData.data) ? clinicsData.data : [clinicsData.data]) : [];
  const clinic = clinics.find((c) => c.id === clinicId);

  const address =
    clinic && clinic.location
      ? formatAddress({
          addressLine1: toTitleCase(clinic.location.address_line_1) || undefined,
          addressLine2: toTitleCase(clinic.location.address_line_2 ?? "") || undefined,
          city: toTitleCase(clinic.location.city),
          state: clinic.location.state,
          zip: clinic.location.postal_code,
        })
      : null;

  if (isLoading) return null;

  return (
    <div className="flex flex-col bg-bg-default min-h-full px-4 sm:px-8 lg:px-16 py-8">
      <Breadcrumb
        crumbs={[
          { label: "Organizations", to: "/organizations" },
          { label: org?.name ?? "...", to: `/organizations/${orgId}?tab=clinic-listings` },
          { label: clinic?.name ?? "..." },
        ]}
      />

      {/* Header */}
      <div className="flex flex-row items-center gap-3 mb-1">
        <Heading size="medium">{clinic?.name}</Heading>
        {clinic && <div>{renderVerificationStatus(clinic.verification_status)}</div>}
      </div>
      <Paragraph size="small" colorType="secondary" className="mb-6">
        Review clinic details
      </Paragraph>

      {/* Content grid */}
      <div className="grid grid-cols-4 gap-8">
        {/* Clinic Information */}
        <div className="flex flex-col col-span-3 gap-4 p-8 bg-bg-surface border border-border-medium rounded-xl">
          <Heading size="small">Clinic Information</Heading>

          <div className="flex flex-col">
            <Label size="small" colorType="secondary">
              Name
            </Label>
            <Paragraph colorType="primary">{clinic?.name ?? "—"}</Paragraph>
          </div>

          <div className="flex flex-col">
            <Label size="small" colorType="secondary">
              Address
            </Label>
            <Paragraph colorType="primary">{address || "—"}</Paragraph>
          </div>

          {clinic?.website_url && (
            <div className="flex flex-col">
              <Label size="small" colorType="secondary">
                Website
              </Label>
              <a
                href={clinic.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-rio-600 hover:underline truncate max-w-xs"
              >
                {clinic.website_url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              </a>
            </div>
          )}

          {clinic?.phone_number && (
            <div className="flex flex-col">
              <Label size="small" colorType="secondary">
                Phone
              </Label>
              <Paragraph colorType="primary">{clinic.phone_number}</Paragraph>
            </div>
          )}

          {clinic?.contact_email && (
            <div className="flex flex-col">
              <Label size="small" colorType="secondary">
                Contact Email
              </Label>
              <Paragraph colorType="primary">{clinic.contact_email}</Paragraph>
            </div>
          )}

          {clinic?.description && (
            <div className="flex flex-col">
              <Label size="small" colorType="secondary">
                About
              </Label>
              <Paragraph colorType="primary">{clinic.description}</Paragraph>
            </div>
          )}

          {clinic?.services_offered && clinic.services_offered.length > 0 && (
            <div className="flex flex-col gap-2">
              <Label size="small" colorType="secondary">
                Services
              </Label>
              <div className="flex flex-row gap-2 flex-wrap">
                {clinic.services_offered.map((s) => (
                  <div key={s} className="px-3 py-1 rounded-3xl bg-limestone-100">
                    <Paragraph size="small" colorType="secondary">
                      {toTitleCase(s.replace(/_/g, " "))}
                    </Paragraph>
                  </div>
                ))}
              </div>
            </div>
          )}

          {clinic?.modality_types && clinic.modality_types.length > 0 && (
            <div className="flex flex-col gap-2">
              <Label size="small" colorType="secondary">
                Modalities
              </Label>
              <div className="flex flex-row gap-2 flex-wrap">
                {clinic.modality_types.map((m) => (
                  <div key={m} className="px-3 py-1 rounded-3xl bg-limestone-100">
                    <Paragraph size="small" colorType="secondary">
                      {toTitleCase(m.replace(/_/g, " "))}
                    </Paragraph>
                  </div>
                ))}
              </div>
            </div>
          )}

          {clinic?.payors && clinic.payors.length > 0 && (
            <div className="flex flex-col gap-2">
              <Label size="small" colorType="secondary">
                Insurance Accepted
              </Label>
              <div className="flex flex-row gap-2 flex-wrap">
                {clinic.payors.map((p) => (
                  <div key={p} className="px-3 py-1 rounded-3xl bg-limestone-100">
                    <Paragraph size="small" colorType="secondary">
                      {toTitleCase(p.replace(/_/g, " "))}
                    </Paragraph>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Request Status */}
        <div className="flex flex-col gap-2 p-8 bg-bg-surface border border-border-medium rounded-xl self-start">
          <Heading size="small">Request Status</Heading>

          {clinic?.verification_status === VerificationStatusEnum.VERIFIED ? (
            <Paragraph colorType="secondary">This clinic has been verified.</Paragraph>
          ) : clinic?.verification_status === VerificationStatusEnum.PENDING ? (
            <div className="flex flex-col gap-1">
              <Paragraph colorType="secondary">Request submitted</Paragraph>
              <Paragraph colorType="primary">
                {format(new Date(clinic.created_date), "MMMM d, yyyy, h:mmaaa")}
              </Paragraph>
            </div>
          ) : (
            <Paragraph colorType="secondary">No verification request has been submitted.</Paragraph>
          )}
        </div>
      </div>
    </div>
  );
};
